import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, X, Shield, MapPin, Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import { getPropertyCurrency, formatConvertedPrice } from '../utils/propertyPrice';
import { useSEO } from '../utils/seo';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---- Sub-Components ----

const SearchBar = ({ searchQuery, onSearchInput, suggestions, showSuggestions, onSelectSuggestion, onFocus, searchRef, country, exchangeRates }) => (
  <div className="flex-1 relative" ref={searchRef}>
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10" />
    <Input
      type="text"
      placeholder="Search by property name, city..."
      value={searchQuery}
      onChange={(e) => onSearchInput(e.target.value)}
      onFocus={onFocus}
      className="pl-12 bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] placeholder:text-slate-400 h-12 text-base"
      data-testid="search-city-input"
    />
    {showSuggestions && suggestions.length > 0 && (
      <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 border-t-0 z-50 shadow-lg max-h-72 overflow-y-auto" data-testid="search-suggestions">
        {suggestions.map((s) => (
          <button
            key={s.id}
            type="button"
            className="w-full text-left px-4 py-3 hover:bg-[#FEF3EC] border-b border-slate-100 last:border-0 flex items-center gap-3 transition-colors"
            onClick={() => onSelectSuggestion(s)}
            data-testid={`suggestion-${s.id}`}
          >
            <MapPin className="h-4 w-4 text-[#E07A3A] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[#1A1A1A] font-medium text-sm truncate">{s.title}</p>
              <p className="text-[#1A1A1A]/50 text-xs">{s.city}</p>
            </div>
            <span className="text-[#E07A3A] font-semibold text-sm flex-shrink-0">{formatConvertedPrice(s.price, getPropertyCurrency(s), country, exchangeRates)}</span>
          </button>
        ))}
      </div>
    )}
  </div>
);

const PropertyFiltersPanel = ({ filters, onChange, onClear }) => (
  <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    <div>
      <label className="text-slate-500 text-xs tracking-wider uppercase mb-2 block">Property Type</label>
      <Select value={filters.property_type} onValueChange={(v) => onChange('property_type', v)}>
        <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#0F172A] h-11" data-testid="filter-property-type">
          <SelectValue placeholder="Any Type" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200">
          <SelectItem value="apartment">Apartment</SelectItem>
          <SelectItem value="house">House</SelectItem>
          <SelectItem value="villa">Villa</SelectItem>
          <SelectItem value="penthouse">Penthouse</SelectItem>
          <SelectItem value="studio">Studio</SelectItem>
          <SelectItem value="commercial">Commercial</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <label className="text-slate-500 text-xs tracking-wider uppercase mb-2 block">Listing Type</label>
      <Select value={filters.listing_type} onValueChange={(v) => onChange('listing_type', v)}>
        <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#0F172A] h-11" data-testid="filter-listing-type">
          <SelectValue placeholder="Sale/Rent" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200">
          <SelectItem value="sale">For Sale</SelectItem>
          <SelectItem value="rent">For Rent</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <label className="text-slate-500 text-xs tracking-wider uppercase mb-2 block">Min Bedrooms</label>
      <Select value={filters.bedrooms} onValueChange={(v) => onChange('bedrooms', v)}>
        <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#0F172A] h-11" data-testid="filter-bedrooms">
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200">
          <SelectItem value="1">1+</SelectItem>
          <SelectItem value="2">2+</SelectItem>
          <SelectItem value="3">3+</SelectItem>
          <SelectItem value="4">4+</SelectItem>
          <SelectItem value="5">5+</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="flex gap-2">
      <div className="flex-1">
        <label className="text-slate-500 text-xs tracking-wider uppercase mb-2 block">Min Price</label>
        <Input type="number" placeholder="Min" value={filters.min_price} onChange={(e) => onChange('min_price', e.target.value)}
          className="bg-[#F8FAFC] border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-11" data-testid="filter-min-price" />
      </div>
      <div className="flex-1">
        <label className="text-slate-500 text-xs tracking-wider uppercase mb-2 block">Max Price</label>
        <Input type="number" placeholder="Max" value={filters.max_price} onChange={(e) => onChange('max_price', e.target.value)}
          className="bg-[#F8FAFC] border-slate-200 text-[#0F172A] placeholder:text-slate-300 h-11" data-testid="filter-max-price" />
      </div>
    </div>
    <div className="sm:col-span-2 lg:col-span-4 flex justify-end pt-2">
      <Button variant="ghost" onClick={onClear} className="text-slate-500 hover:text-[#0F172A] hover:bg-slate-50 tracking-wider" data-testid="clear-filters-btn">
        <X className="h-4 w-4 mr-2" />CLEAR FILTERS
      </Button>
    </div>
  </div>
);

const PropertyGrid = ({ properties, loading, onClearFilters }) => {
  if (loading) {
    return (
      <div className="property-grid">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={`skeleton-${i}`} className="luxury-card h-96 animate-pulse">
            <div className="h-64 bg-slate-100" />
            <div className="p-5 space-y-3">
              <div className="h-4 bg-slate-100 w-1/3" />
              <div className="h-4 bg-slate-100 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-24 luxury-card">
        <Shield className="h-16 w-16 text-slate-200 mx-auto mb-4" />
        <h3 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-3">No Properties Found</h3>
        <p className="text-slate-500 mb-8">Try adjusting your filters or search criteria</p>
        <Button onClick={onClearFilters} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-wider">CLEAR ALL FILTERS</Button>
      </div>
    );
  }

  return (
    <>
      <p className="text-slate-400 mb-8 text-sm tracking-wider">{properties.length} properties found</p>
      <div className="property-grid">
        {properties.map((property, index) => (
          <div key={property.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
            <PropertyCard property={property} />
          </div>
        ))}
      </div>
    </>
  );
};

// ---- Main Component ----

const INITIAL_FILTERS = { city: '', property_type: '', listing_type: '', min_price: '', max_price: '', bedrooms: '' };

const PropertiesPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const searchRef = useRef(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const { country, exchangeRates } = useCountry();
  const { user } = useAuth();

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', alert_frequency: 'daily' });
  const [savingSearch, setSavingSearch] = useState(false);

  useSEO({
    title: 'Browse Properties for Sale & Rent — UK, India, Canada',
    description: 'Explore verified property listings with 360° tours across UK, India and Canada. Houses, flats, apartments for sale and rent. No estate agent fees — save thousands with Shopperden.',
    canonical: 'https://shopperden.com/properties',
  });

  const fetchProperties = useCallback(async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await axios.get(`${API_URL}/api/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProperties(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearchQuery('');
    fetchProperties();
  };

  const handleSearchInput = (val) => {
    setSearchQuery(val);
    handleFilterChange('city', val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/properties/search/suggest?q=${encodeURIComponent(val)}`);
        setSuggestions(res.data);
        setShowSuggestions(res.data.length > 0);
      } catch (e) { logger.error('Search suggest failed:', e); }
    }, 300);
  };

  const selectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery(suggestion.title);
    navigate(`/property/${suggestion.id}`);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const openSaveModal = () => {
    if (!user) {
      toast.error('Sign in to save searches and get email alerts');
      navigate('/login');
      return;
    }
    if (user.role && !['buyer', 'tenant'].includes(user.role)) {
      toast.error('Saved searches are for buyers and tenants only');
      return;
    }
    if (!hasActiveFilters) {
      toast.error('Apply at least one filter to save a search');
      return;
    }
    const autoName = [filters.city, filters.property_type, filters.listing_type && `for ${filters.listing_type}`]
      .filter(Boolean).join(' · ') || 'My search';
    setSaveForm({ name: autoName, alert_frequency: 'daily' });
    setShowSaveModal(true);
  };

  const handleSaveSearch = async () => {
    setSavingSearch(true);
    try {
      const payload = {
        name: saveForm.name,
        alert_frequency: saveForm.alert_frequency,
        city: filters.city || null,
        property_type: filters.property_type || null,
        listing_type: filters.listing_type || null,
        bedrooms: filters.bedrooms ? parseInt(filters.bedrooms, 10) : null,
        min_price: filters.min_price ? parseFloat(filters.min_price) : null,
        max_price: filters.max_price ? parseFloat(filters.max_price) : null,
        country: country || null,
      };
      await axios.post(`${API_URL}/api/saved-searches`, payload);
      toast.success('Search saved — you\'ll get email alerts for new matches');
      setShowSaveModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSearch(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">Our Collection</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A] mb-2 font-semibold">All Properties</h1>
          <p className="text-[#1A1A1A]/60 text-base font-medium mb-4">Discover properties across UK, India and Canada</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/properties/uk" className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-[#E07A3A]/10 hover:text-[#E07A3A] text-slate-600 text-xs tracking-wider uppercase font-bold transition-colors" data-testid="country-link-uk">
              {'\u{1F1EC}\u{1F1E7}'} United Kingdom
            </Link>
            <Link to="/properties/india" className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-[#E07A3A]/10 hover:text-[#E07A3A] text-slate-600 text-xs tracking-wider uppercase font-bold transition-colors" data-testid="country-link-india">
              {'\u{1F1EE}\u{1F1F3}'} India
            </Link>
            <Link to="/properties/canada" className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-[#E07A3A]/10 hover:text-[#E07A3A] text-slate-600 text-xs tracking-wider uppercase font-bold transition-colors" data-testid="country-link-canada">
              {'\u{1F1E8}\u{1F1E6}'} Canada
            </Link>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="luxury-card p-5 mb-10">
          <div className="flex flex-col md:flex-row gap-4">
            <SearchBar
              searchQuery={searchQuery}
              onSearchInput={handleSearchInput}
              suggestions={suggestions}
              showSuggestions={showSuggestions}
              onSelectSuggestion={selectSuggestion}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              searchRef={searchRef}
              country={country}
              exchangeRates={exchangeRates}
            />
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-slate-200 text-slate-600 hover:bg-slate-50 h-12 tracking-wider" data-testid="toggle-filters-btn">
              <SlidersHorizontal className="h-4 w-4 mr-2" />FILTERS
            </Button>
            <Button onClick={applyFilters} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 px-8 tracking-wider" data-testid="apply-filters-btn">SEARCH</Button>
            <Button onClick={openSaveModal} variant="outline" className="border-[#E07A3A]/30 text-[#E07A3A] hover:bg-[#E07A3A]/5 h-12 tracking-wider" data-testid="save-search-btn">
              <Bell className="h-4 w-4 mr-2" />SAVE SEARCH
            </Button>
          </div>
          {showFilters && <PropertyFiltersPanel filters={filters} onChange={handleFilterChange} onClear={clearFilters} />}
        </div>

        <PropertyGrid properties={properties} loading={loading} onClearFilters={clearFilters} />
      </div>

      {showSaveModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !savingSearch && setShowSaveModal(false)}>
          <div className="bg-white max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="save-search-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#E07A3A]" /> Save this search
            </h3>
            <p className="text-xs text-slate-500 mb-4">We&apos;ll email you when new properties match.</p>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Name</label>
            <input
              value={saveForm.name}
              onChange={e => setSaveForm({ ...saveForm, name: e.target.value })}
              maxLength={80}
              className="w-full border border-slate-300 px-3 py-2 text-sm mb-4"
              data-testid="save-search-name"
            />
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Alert frequency</label>
            <select
              value={saveForm.alert_frequency}
              onChange={e => setSaveForm({ ...saveForm, alert_frequency: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm mb-4"
              data-testid="save-search-frequency"
            >
              <option value="instant">Instant — as soon as a match appears</option>
              <option value="daily">Daily digest (recommended)</option>
              <option value="weekly">Weekly digest</option>
              <option value="off">No emails — manual check only</option>
            </select>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowSaveModal(false)} disabled={savingSearch} data-testid="save-search-cancel">
                Cancel
              </Button>
              <Button onClick={handleSaveSearch} disabled={savingSearch || !saveForm.name.trim()} className="bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="save-search-confirm">
                {savingSearch ? <><Loader2 className="h-3 w-3 animate-spin mr-2" />Saving</> : 'Save & alert me'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
